/**
 * Share Book Setup Wizard
 *
 * Multi-step wizard for creating a new share book (aktiebok).
 * Collects: share capital, clauses, share classes, initial shareholders (multiple founders).
 */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  ChevronRight,
  ChevronLeft,
  Check,
  Building2,
  Users,
  FileText,
  AlertCircle,
  Search,
  Loader2,
  Info,
  CheckCircle,
  Hash,
  Plus,
  Trash2,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCreateShareBook, useCreateShareClass, useCreateShareholder, useRegisterFoundingTransaction } from "@/hooks/use-share-book";
import { companiesApi, reportsApi, type ShareholderType } from "@/lib/api";

// Generate a unique ID (fallback for environments without crypto.randomUUID)
function generateId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback: generate a pseudo-UUID
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface ShareBookSetupProps {
  companyId: string;
  companyName: string;
  onComplete: () => void;
}

// Step 1: Basic share book info
const shareBookSchema = z.object({
  shareCapital: z.string().min(1, "Aktiekapital krävs").refine(
    (val) => !isNaN(Number(val)) && Number(val) >= 25000,
    "Minsta aktiekapital för AB är 25 000 kr"
  ),
  hasPreemptionClause: z.boolean(),
  hasConsentClause: z.boolean(),
  hasRedemptionClause: z.boolean(),
  hasConversionClause: z.boolean(),
});

// Step 2: Share class
const shareClassSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  code: z.string().min(1, "Kod krävs").max(5, "Max 5 tecken"),
  votingPower: z.string().refine(
    (val) => !isNaN(Number(val)) && Number(val) > 0,
    "Röststyrka måste vara större än 0"
  ),
  parValue: z.string().optional(),
  totalShares: z.string().min(1, "Antal aktier krävs").refine(
    (val) => !isNaN(Number(val)) && Number(val) > 0 && Number.isInteger(Number(val)),
    "Antal aktier måste vara ett heltal större än 0"
  ),
});

// Normalize personnummer/orgnumber for comparison (removes all non-digits)
function normalizeIdNumber(input: string | undefined): string {
  if (!input) return "";
  return input.replace(/\D/g, "");
}

// Check if a personnummer/orgnumber already exists in the founders list
function isDuplicateIdNumber(
  newId: string | undefined,
  existingFounders: FounderData[],
  editingIndex: number | null,
  isPersonalId: boolean
): { isDuplicate: boolean; existingName?: string } {
  if (!newId || newId.trim().length === 0) {
    return { isDuplicate: false };
  }

  const normalizedNew = normalizeIdNumber(newId);
  if (normalizedNew.length === 0) {
    return { isDuplicate: false };
  }

  for (let i = 0; i < existingFounders.length; i++) {
    // Skip the founder being edited
    if (editingIndex !== null && i === editingIndex) {
      continue;
    }

    const founder = existingFounders[i];
    const existingId = isPersonalId ? founder.personalId : founder.orgNumber;
    const normalizedExisting = normalizeIdNumber(existingId);

    if (normalizedNew === normalizedExisting && normalizedExisting.length > 0) {
      return { isDuplicate: true, existingName: founder.name };
    }
  }

  return { isDuplicate: false };
}

// Personnummer validation (Luhn algorithm)
function validatePersonnummer(input: string): { valid: boolean; error?: string } {
  if (!input || typeof input !== "string") {
    return { valid: false, error: "Personnummer saknas" };
  }

  // Clean input - remove spaces, hyphens, plus
  let cleaned = input.replace(/[\s\-+]/g, "").trim();

  if (cleaned.length === 0) {
    return { valid: false, error: "Personnummer saknas" };
  }

  // Check that it's only digits
  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, error: "Personnummer får endast innehålla siffror" };
  }

  // Normalize to 10 digits for Luhn check
  let tenDigit: string;
  if (cleaned.length === 12) {
    tenDigit = cleaned.substring(2); // Remove century
  } else if (cleaned.length === 10) {
    tenDigit = cleaned;
  } else {
    return { valid: false, error: `Fel format: förväntar 10 eller 12 siffror, fick ${cleaned.length}` };
  }

  // Validate date parts (basic check)
  const month = parseInt(tenDigit.substring(2, 4), 10);
  let day = parseInt(tenDigit.substring(4, 6), 10);

  // Check for samordningsnummer (day + 60)
  if (day > 60) {
    day -= 60;
  }

  if (month < 1 || month > 12) {
    return { valid: false, error: "Ogiltig månad i personnummer" };
  }

  if (day < 1 || day > 31) {
    return { valid: false, error: "Ogiltig dag i personnummer" };
  }

  // Luhn algorithm
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    let digit = parseInt(tenDigit[i], 10);
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }

  if (sum % 10 !== 0) {
    return { valid: false, error: "Ogiltig kontrollsiffra" };
  }

  return { valid: true };
}

// Step 3: Initial shareholder (founder)
// According to ABL 5 kap 2§, the share book must contain personal ID or organization number
const shareholderSchema = z.object({
  type: z.enum(["person", "company", "estate", "trust"]),
  name: z.string().min(1, "Namn krävs"),
  personalId: z.string().optional(),
  orgNumber: z.string().optional(),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  email: z.string().email("Ogiltig e-postadress").optional().or(z.literal("")),
  phone: z.string().optional(),
  shareQuantity: z.string().min(1, "Antal aktier krävs").refine(
    (val) => !isNaN(Number(val)) && Number(val) > 0,
    "Antal aktier måste vara större än 0"
  ),
}).refine(
  (data) => {
    // Person type requires personalId
    if (data.type === "person") {
      return data.personalId && data.personalId.trim().length > 0;
    }
    return true;
  },
  {
    message: "Personnummer krävs för privatpersoner (ABL 5 kap 2§)",
    path: ["personalId"],
  }
).refine(
  (data) => {
    // Validate personnummer format if provided for person type
    if (data.type === "person" && data.personalId && data.personalId.trim().length > 0) {
      const result = validatePersonnummer(data.personalId);
      return result.valid;
    }
    return true;
  },
  {
    message: "Ogiltigt personnummer - kontrollera att alla siffror är korrekta",
    path: ["personalId"],
  }
).refine(
  (data) => {
    // Company, estate, and trust types require orgNumber
    if (data.type === "company" || data.type === "estate" || data.type === "trust") {
      return data.orgNumber && data.orgNumber.trim().length > 0;
    }
    return true;
  },
  {
    message: "Organisationsnummer krävs (ABL 5 kap 2§)",
    path: ["orgNumber"],
  }
);

// Founder with unique ID for list management
interface FounderData {
  id: string;
  type: "person" | "company" | "estate" | "trust";
  name: string;
  personalId?: string;
  orgNumber?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  email?: string;
  phone?: string;
  shareQuantity: string;
}

type ShareBookFormData = z.infer<typeof shareBookSchema>;
type ShareClassFormData = z.infer<typeof shareClassSchema>;
type ShareholderFormData = z.infer<typeof shareholderSchema>;

const STEPS = [
  { id: 1, title: "Grunduppgifter", icon: Building2 },
  { id: 2, title: "Aktieslag", icon: FileText },
  { id: 3, title: "Grundare", icon: Users },
  { id: 4, title: "Bekräfta", icon: Check },
];

const SHAREHOLDER_TYPE_LABELS: Record<ShareholderType, string> = {
  person: "Fysisk person",
  company: "Juridisk person",
  estate: "Dödsbo",
  trust: "Stiftelse/Trust",
};

export function ShareBookSetup({ companyId, companyName, onComplete }: ShareBookSetupProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [shareBookData, setShareBookData] = useState<ShareBookFormData | null>(null);
  const [shareClassData, setShareClassData] = useState<ShareClassFormData | null>(null);
  // Support multiple founders
  const [founders, setFounders] = useState<FounderData[]>([]);
  const [editingFounderIndex, setEditingFounderIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupComplete, setLookupComplete] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [shareCapitalSource, setShareCapitalSource] = useState<string | null>(null);

  const createShareBook = useCreateShareBook();
  const createShareClass = useCreateShareClass();
  const createShareholder = useCreateShareholder();
  const registerFoundingTransaction = useRegisterFoundingTransaction();

  // Fetch annual reports to get SIE data for share capital pre-fill
  const { data: reportsData } = useQuery({
    queryKey: ["reports", companyId],
    queryFn: async () => {
      const response = await reportsApi.list(companyId);
      if (response.success && response.data) {
        return response.data.reports;
      }
      return [];
    },
    enabled: !!companyId,
  });

  // Get the most recent report with SIE data to extract share capital
  const { data: latestReportWithSie } = useQuery({
    queryKey: ["report-sie", companyId, reportsData?.[0]?.id],
    queryFn: async () => {
      // Get the most recent report
      const reportId = reportsData?.[0]?.id;
      if (!reportId) return null;

      const response = await reportsApi.get(reportId);
      if (response.success && response.data?.report?.parsedSieData) {
        return response.data.report;
      }
      return null;
    },
    enabled: !!reportsData && reportsData.length > 0,
  });

  // Step 1 form
  const shareBookForm = useForm<ShareBookFormData>({
    resolver: zodResolver(shareBookSchema),
    defaultValues: {
      shareCapital: "25000",
      hasPreemptionClause: false,
      hasConsentClause: false,
      hasRedemptionClause: false,
      hasConversionClause: false,
    },
  });

  // Step 2 form
  const shareClassForm = useForm<ShareClassFormData>({
    resolver: zodResolver(shareClassSchema),
    defaultValues: {
      name: "Stamaktie",
      code: "STAM",
      votingPower: "1",
      parValue: "",
      totalShares: "1000",
    },
  });

  // Step 3 form
  const shareholderForm = useForm<ShareholderFormData>({
    resolver: zodResolver(shareholderSchema),
    defaultValues: {
      type: "person",
      name: "",
      personalId: "",
      orgNumber: "",
      address: "",
      postalCode: "",
      city: "",
      email: "",
      phone: "",
      shareQuantity: "",
    },
  });

  // Pre-fill share capital from SIE data when available
  useEffect(() => {
    if (latestReportWithSie?.parsedSieData) {
      const sieShareCapital = latestReportWithSie.parsedSieData.balanceSheet?.equityAndLiabilities?.equity?.shareCapital;
      if (sieShareCapital && sieShareCapital > 0) {
        const currentValue = shareBookForm.getValues("shareCapital");
        // Only update if still at default value
        if (currentValue === "25000") {
          shareBookForm.setValue("shareCapital", String(sieShareCapital));
          const fiscalYear = latestReportWithSie.fiscalYearEnd
            ? new Date(latestReportWithSie.fiscalYearEnd).getFullYear()
            : null;
          setShareCapitalSource(fiscalYear ? `Hämtat från SIE-fil (${fiscalYear})` : "Hämtat från SIE-fil");
        }
      }
    }
  }, [latestReportWithSie, shareBookForm]);

  // Reset lookup state when shareholder type changes
  const shareholderType = shareholderForm.watch("type");
  useEffect(() => {
    setLookupComplete(false);
    setLookupError(null);
  }, [shareholderType]);

  const handleStep1Submit = (data: ShareBookFormData) => {
    setShareBookData(data);
    // Pre-fill totalShares based on shareCapital (assuming parValue = 1 kr)
    const shareCapital = Number(data.shareCapital);
    // Use shouldValidate and shouldDirty to ensure the form updates correctly
    shareClassForm.setValue("totalShares", String(shareCapital), {
      shouldValidate: true,
      shouldDirty: true
    });
    setCurrentStep(2);
  };

  const handleStep2Submit = (data: ShareClassFormData) => {
    setShareClassData(data);
    // If no founders yet, pre-fill shareholder quantity with total shares for first founder
    if (founders.length === 0) {
      shareholderForm.setValue("shareQuantity", data.totalShares);
    }
    setCurrentStep(3);
  };

  // Add or update a founder
  const handleAddFounder = (data: ShareholderFormData) => {
    // Check for duplicate personnummer
    if (data.type === "person" && data.personalId) {
      const duplicateCheck = isDuplicateIdNumber(
        data.personalId,
        founders,
        editingFounderIndex,
        true
      );
      if (duplicateCheck.isDuplicate) {
        shareholderForm.setError("personalId", {
          type: "manual",
          message: `Personnumret används redan av ${duplicateCheck.existingName}`,
        });
        return;
      }
    }

    // Check for duplicate organisationsnummer
    if (data.type !== "person" && data.orgNumber) {
      const duplicateCheck = isDuplicateIdNumber(
        data.orgNumber,
        founders,
        editingFounderIndex,
        false
      );
      if (duplicateCheck.isDuplicate) {
        shareholderForm.setError("orgNumber", {
          type: "manual",
          message: `Organisationsnumret används redan av ${duplicateCheck.existingName}`,
        });
        return;
      }
    }

    const newFounder: FounderData = {
      id: editingFounderIndex !== null ? founders[editingFounderIndex].id : generateId(),
      type: data.type,
      name: data.name,
      personalId: data.personalId || undefined,
      orgNumber: data.orgNumber || undefined,
      address: data.address || undefined,
      postalCode: data.postalCode || undefined,
      city: data.city || undefined,
      email: data.email || undefined,
      phone: data.phone || undefined,
      shareQuantity: data.shareQuantity,
    };

    if (editingFounderIndex !== null) {
      // Update existing founder
      const updatedFounders = [...founders];
      updatedFounders[editingFounderIndex] = newFounder;
      setFounders(updatedFounders);
      setEditingFounderIndex(null);
    } else {
      // Add new founder
      setFounders([...founders, newFounder]);
    }

    // Reset form for next founder
    shareholderForm.reset({
      type: "person",
      name: "",
      personalId: "",
      orgNumber: "",
      address: "",
      postalCode: "",
      city: "",
      email: "",
      phone: "",
      shareQuantity: "",
    });
    setLookupComplete(false);
    setLookupError(null);
  };

  // Edit an existing founder
  const handleEditFounder = (index: number) => {
    const founder = founders[index];
    shareholderForm.reset({
      type: founder.type,
      name: founder.name,
      personalId: founder.personalId || "",
      orgNumber: founder.orgNumber || "",
      address: founder.address || "",
      postalCode: founder.postalCode || "",
      city: founder.city || "",
      email: founder.email || "",
      phone: founder.phone || "",
      shareQuantity: founder.shareQuantity,
    });
    setEditingFounderIndex(index);
    setLookupComplete(false);
    setLookupError(null);
  };

  // Remove a founder
  const handleRemoveFounder = (index: number) => {
    const updatedFounders = founders.filter((_, i) => i !== index);
    setFounders(updatedFounders);
    if (editingFounderIndex === index) {
      setEditingFounderIndex(null);
      shareholderForm.reset();
    } else if (editingFounderIndex !== null && editingFounderIndex > index) {
      setEditingFounderIndex(editingFounderIndex - 1);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingFounderIndex(null);
    shareholderForm.reset({
      type: "person",
      name: "",
      personalId: "",
      orgNumber: "",
      address: "",
      postalCode: "",
      city: "",
      email: "",
      phone: "",
      shareQuantity: "",
    });
    setLookupComplete(false);
    setLookupError(null);
  };

  // Calculate total shares assigned to founders
  const totalAssignedShares = founders.reduce((sum, f) => sum + Number(f.shareQuantity || 0), 0);
  const totalShares = Number(shareClassData?.totalShares || 0);
  const remainingShares = totalShares - totalAssignedShares;
  const allSharesAssigned = totalShares > 0 && remainingShares === 0;

  // Proceed to confirmation step
  const handleStep3Continue = () => {
    if (founders.length === 0) {
      setError("Du måste lägga till minst en grundare");
      return;
    }
    if (!allSharesAssigned) {
      setError(`Alla aktier måste fördelas. ${remainingShares.toLocaleString("sv-SE")} aktier återstår.`);
      return;
    }

    // Final validation: Check for duplicate personnummer/orgnumber in the founders list
    const seenPersonalIds = new Map<string, string>(); // normalizedId -> name
    const seenOrgNumbers = new Map<string, string>(); // normalizedId -> name

    for (const founder of founders) {
      if (founder.type === "person" && founder.personalId) {
        const normalized = normalizeIdNumber(founder.personalId);
        if (normalized.length > 0) {
          if (seenPersonalIds.has(normalized)) {
            setError(
              `Dubblett: Personnumret ${founder.personalId} används av både ${seenPersonalIds.get(normalized)} och ${founder.name}`
            );
            return;
          }
          seenPersonalIds.set(normalized, founder.name);
        }
      } else if (founder.orgNumber) {
        const normalized = normalizeIdNumber(founder.orgNumber);
        if (normalized.length > 0) {
          if (seenOrgNumbers.has(normalized)) {
            setError(
              `Dubblett: Organisationsnumret ${founder.orgNumber} används av både ${seenOrgNumbers.get(normalized)} och ${founder.name}`
            );
            return;
          }
          seenOrgNumbers.set(normalized, founder.name);
        }
      }
    }

    // Validate all personnummer formats before proceeding
    for (const founder of founders) {
      if (founder.type === "person" && founder.personalId) {
        const result = validatePersonnummer(founder.personalId);
        if (!result.valid) {
          setError(`Ogiltigt personnummer för ${founder.name}: ${result.error}`);
          return;
        }
      }
    }

    setError(null);
    setCurrentStep(4);
  };

  // Lookup company from Bolagsverket
  const handleCompanyLookup = async () => {
    const orgNumber = shareholderForm.getValues("orgNumber");
    if (!orgNumber || orgNumber.length < 10) {
      setLookupError("Ange ett giltigt organisationsnummer");
      return;
    }

    setIsLookingUp(true);
    setLookupError(null);

    try {
      const result = await companiesApi.lookup(orgNumber);

      if (result.success && result.data) {
        const company = result.data;
        shareholderForm.setValue("name", company.name);
        if (company.address?.street) shareholderForm.setValue("address", company.address.street);
        if (company.address?.postalCode) shareholderForm.setValue("postalCode", company.address.postalCode);
        if (company.address?.city) shareholderForm.setValue("city", company.address.city);
        setLookupError(null);
        setLookupComplete(true);
      } else {
        setLookupError(result.error?.message || "Kunde inte hitta företaget");
        setLookupComplete(false);
      }
    } catch {
      setLookupError("Ett fel uppstod vid sökningen");
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (!shareBookData || !shareClassData || founders.length === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Step 1: Create share book
      await createShareBook.mutateAsync({
        companyId,
        data: {
          shareCapital: Number(shareBookData.shareCapital),
          hasPreemptionClause: shareBookData.hasPreemptionClause,
          hasConsentClause: shareBookData.hasConsentClause,
          hasRedemptionClause: shareBookData.hasRedemptionClause,
          hasConversionClause: shareBookData.hasConversionClause,
        },
      });

      // Step 2: Create share class
      const shareClass = await createShareClass.mutateAsync({
        companyId,
        data: {
          name: shareClassData.name,
          code: shareClassData.code,
          votingPower: Number(shareClassData.votingPower),
          parValue: shareClassData.parValue ? Number(shareClassData.parValue) : undefined,
          totalShares: Number(shareClassData.totalShares),
          numberSeriesStart: 1,
        },
      });

      // Calculate price per share from share capital / total shares
      const pricePerShare = Number(shareBookData.shareCapital) / Number(shareClassData.totalShares);
      const transactionDate = new Date().toISOString().split("T")[0];

      // Step 3 & 4: Create each founder and register founding transaction
      const createdShareholders: { id: string; name: string }[] = [];

      for (let i = 0; i < founders.length; i++) {
        const founder = founders[i];
        try {
          // Create shareholder
          const shareholder = await createShareholder.mutateAsync({
            companyId,
            data: {
              type: founder.type,
              name: founder.name,
              personalId: founder.personalId || undefined,
              orgNumber: founder.orgNumber || undefined,
              address: founder.address || undefined,
              postalCode: founder.postalCode || undefined,
              city: founder.city || undefined,
              email: founder.email || undefined,
              phone: founder.phone || undefined,
            },
          });

          createdShareholders.push({ id: shareholder.id, name: founder.name });

          // Register founding transaction for this shareholder
          await registerFoundingTransaction.mutateAsync({
            companyId,
            data: {
              toShareholderId: shareholder.id,
              shareClassId: shareClass.id,
              quantity: Number(founder.shareQuantity),
              pricePerShare,
              transactionDate,
            },
          });
        } catch (founderErr) {
          // Build an error message that shows which founder failed
          const errorMsg = founderErr instanceof Error ? founderErr.message : "Okänt fel";
          const founderIndex = i + 1;
          const totalFounders = founders.length;

          if (createdShareholders.length > 0) {
            throw new Error(
              `Fel vid skapande av grundare ${founderIndex}/${totalFounders} (${founder.name}): ${errorMsg}. ` +
              `${createdShareholders.length} grundare skapades innan felet.`
            );
          } else {
            throw new Error(
              `Fel vid skapande av grundare ${founderIndex}/${totalFounders} (${founder.name}): ${errorMsg}`
            );
          }
        }
      }

      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ett fel uppstod vid skapandet av aktieboken");
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="card max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-blue-100 rounded-lg">
          <BookOpen className="text-blue-600" size={24} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Skapa aktiebok</h2>
          <p className="text-sm text-gray-500">{companyName}</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;

          return (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    isCompleted
                      ? "bg-green-500 text-white"
                      : isActive
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                </div>
                <span
                  className={`text-xs mt-1 ${
                    isActive ? "text-blue-600 font-medium" : "text-gray-500"
                  }`}
                >
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`w-12 sm:w-20 h-0.5 mx-2 ${
                    isCompleted ? "bg-green-500" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm font-medium text-red-800">Ett fel uppstod</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Step 1: Basic info */}
      {currentStep === 1 && (
        <form onSubmit={shareBookForm.handleSubmit(handleStep1Submit)}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aktiekapital (SEK) *
              </label>
              <input
                type="text"
                {...shareBookForm.register("shareCapital")}
                className="input"
                placeholder="25000"
              />
              {shareBookForm.formState.errors.shareCapital && (
                <p className="text-sm text-red-600 mt-1">
                  {shareBookForm.formState.errors.shareCapital.message}
                </p>
              )}
              {shareCapitalSource ? (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Info size={12} />
                  {shareCapitalSource}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  Minsta aktiekapital för AB är 25 000 kr enligt ABL 1 kap 5§
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Förbehåll i bolagsordningen
              </label>
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...shareBookForm.register("hasPreemptionClause")}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Förköpsförbehåll (hembudsklausul)
                  </span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...shareBookForm.register("hasConsentClause")}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Samtyckesförbehåll
                  </span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...shareBookForm.register("hasRedemptionClause")}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Inlösenförbehåll
                  </span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...shareBookForm.register("hasConversionClause")}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Omvandlingsförbehåll
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-8">
            <Button type="submit">
              Nästa
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </form>
      )}

      {/* Step 2: Share class */}
      {currentStep === 2 && (
        <form onSubmit={shareClassForm.handleSubmit(handleStep2Submit)}>
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Namn på aktieslag *
                </label>
                <input
                  type="text"
                  {...shareClassForm.register("name")}
                  className="input"
                  placeholder="Stamaktie"
                />
                {shareClassForm.formState.errors.name && (
                  <p className="text-sm text-red-600 mt-1">
                    {shareClassForm.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kod *
                </label>
                <input
                  type="text"
                  {...shareClassForm.register("code")}
                  className="input"
                  placeholder="STAM"
                  maxLength={5}
                />
                {shareClassForm.formState.errors.code && (
                  <p className="text-sm text-red-600 mt-1">
                    {shareClassForm.formState.errors.code.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Röststyrka per aktie *
                </label>
                <input
                  type="text"
                  {...shareClassForm.register("votingPower")}
                  className="input"
                  placeholder="1"
                />
                {shareClassForm.formState.errors.votingPower && (
                  <p className="text-sm text-red-600 mt-1">
                    {shareClassForm.formState.errors.votingPower.message}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  ABL 4 kap 5§: Röstvärdesdifferens max 10:1
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kvotvärde (SEK)
                </label>
                <input
                  type="text"
                  {...shareClassForm.register("parValue")}
                  className="input"
                  placeholder="Beräknas automatiskt"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lämna tomt för automatisk beräkning
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Totalt antal aktier *
              </label>
              <input
                type="text"
                {...shareClassForm.register("totalShares")}
                className="input"
                placeholder="1000"
              />
              {shareClassForm.formState.errors.totalShares && (
                <p className="text-sm text-red-600 mt-1">
                  {shareClassForm.formState.errors.totalShares.message}
                </p>
              )}
              {/* Show recommendation based on share capital */}
              {shareBookData && (
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <Info size={12} />
                  Rekommenderat: {Number(shareBookData.shareCapital).toLocaleString("sv-SE")} aktier (kvotvärde 1 kr/aktie)
                </p>
              )}
              {/* Show calculated kvotvärde */}
              {shareBookData && (() => {
                const totalShares = Number(shareClassForm.watch("totalShares"));
                const shareCapital = Number(shareBookData.shareCapital);
                if (totalShares > 0 && shareCapital > 0) {
                  const calculatedParValue = shareCapital / totalShares;
                  const isValidParValue = calculatedParValue >= 0.01 && Number.isFinite(calculatedParValue);
                  const isRoundNumber = calculatedParValue === Math.floor(calculatedParValue);
                  return (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Beräknat kvotvärde:</span>
                        <span className={`font-medium ${isValidParValue ? "text-gray-900" : "text-red-600"}`}>
                          {isValidParValue ? `${calculatedParValue.toFixed(2)} kr/aktie` : "Ogiltigt"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-gray-600">Aktiekapital:</span>
                        <span className="font-medium text-gray-900">{shareCapital.toLocaleString("sv-SE")} kr</span>
                      </div>
                      {isValidParValue && calculatedParValue !== 1 && (
                        <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {!isRoundNumber
                            ? "Kvotvärdet är inte ett heltal. Vanligtvis är kvotvärdet 1 kr."
                            : `Kvotvärdet är ${calculatedParValue} kr. Vanligtvis är kvotvärdet 1 kr.`}
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>

          <div className="flex justify-between mt-8">
            <Button type="button" variant="outline" onClick={goBack}>
              <ChevronLeft size={16} className="mr-1" />
              Tillbaka
            </Button>
            <Button type="submit">
              Nästa
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </form>
      )}

      {/* Step 3: Multiple founders */}
      {currentStep === 3 && (
        <div>
          {/* Share distribution overview */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Aktiefördelning</span>
              <span className="text-sm text-gray-500">
                {totalShares.toLocaleString("sv-SE")} aktier totalt
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div
                className={`h-2.5 rounded-full transition-all ${
                  allSharesAssigned ? "bg-green-500" : "bg-blue-500"
                }`}
                style={{ width: `${Math.min(100, (totalAssignedShares / totalShares) * 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs">
              <span className={allSharesAssigned ? "text-green-600 font-medium" : "text-gray-500"}>
                {totalAssignedShares.toLocaleString("sv-SE")} fördelade
              </span>
              <span className={remainingShares > 0 ? "text-amber-600 font-medium" : "text-gray-500"}>
                {remainingShares.toLocaleString("sv-SE")} återstår
              </span>
            </div>
          </div>

          {/* List of added founders */}
          {founders.length > 0 && (
            <div className="mb-6 space-y-3">
              <h4 className="text-sm font-medium text-gray-700">
                Grundare ({founders.length})
              </h4>
              {founders.map((founder, index) => (
                <div
                  key={founder.id}
                  className={`p-4 rounded-lg border ${
                    editingFounderIndex === index
                      ? "border-blue-300 bg-blue-50"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{founder.name}</span>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {SHAREHOLDER_TYPE_LABELS[founder.type]}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        {Number(founder.shareQuantity).toLocaleString("sv-SE")} aktier
                        <span className="mx-1">·</span>
                        {((Number(founder.shareQuantity) / totalShares) * 100).toFixed(1)}%
                      </div>
                      {(founder.personalId || founder.orgNumber) && (
                        <div className="mt-1 text-xs text-gray-400">
                          {founder.personalId || founder.orgNumber}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleEditFounder(index)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Redigera"
                      >
                        <FileText size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveFounder(index)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Ta bort"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add/Edit founder form */}
          <form onSubmit={shareholderForm.handleSubmit(handleAddFounder)}>
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="flex items-center gap-2 mb-4">
                <UserPlus size={18} className="text-blue-600" />
                <h4 className="font-medium text-gray-900">
                  {editingFounderIndex !== null ? "Redigera grundare" : "Lägg till grundare"}
                </h4>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Typ av ägare *
                  </label>
                  <select {...shareholderForm.register("type")} className="input">
                    {Object.entries(SHAREHOLDER_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* For non-person types: Show Organisationsnummer first with lookup */}
                {shareholderForm.watch("type") !== "person" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Organisationsnummer *
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Hash size={15} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          {...shareholderForm.register("orgNumber")}
                          className="input pl-9"
                          placeholder="XXXXXX-XXXX"
                          disabled={isLookingUp}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCompanyLookup}
                        disabled={
                          isLookingUp ||
                          !shareholderForm.watch("orgNumber") ||
                          shareholderForm.watch("orgNumber")!.replace(/\D/g, "").length < 10
                        }
                      >
                        {isLookingUp ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <>
                            <Search size={15} />
                            Hämta
                          </>
                        )}
                      </Button>
                    </div>
                    {lookupComplete && (
                      <p className="text-sm text-green-600 mt-1.5 flex items-center gap-1">
                        <CheckCircle size={14} />
                        Uppgifter hämtade
                      </p>
                    )}
                    {lookupError && (
                      <p className="text-sm text-amber-600 mt-1.5">
                        {lookupError}
                      </p>
                    )}
                    {shareholderForm.formState.errors.orgNumber && (
                      <p className="text-sm text-red-600 mt-1">
                        {shareholderForm.formState.errors.orgNumber.message}
                      </p>
                    )}
                  </div>
                )}

                {/* Namn field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Namn *
                  </label>
                  <input
                    type="text"
                    {...shareholderForm.register("name")}
                    className="input"
                    placeholder={shareholderForm.watch("type") === "person" ? "Förnamn Efternamn" : "Företagsnamn AB"}
                  />
                  {shareholderForm.formState.errors.name && (
                    <p className="text-sm text-red-600 mt-1">
                      {shareholderForm.formState.errors.name.message}
                    </p>
                  )}
                </div>

                {/* For person type: Show Personnummer after Namn */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {shareholderForm.watch("type") === "person" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Personnummer *
                      </label>
                      <input
                        type="text"
                        {...shareholderForm.register("personalId")}
                        className="input"
                        placeholder="YYYYMMDD-XXXX"
                      />
                      {shareholderForm.formState.errors.personalId && (
                        <p className="text-sm text-red-600 mt-1">
                          {shareholderForm.formState.errors.personalId.message}
                        </p>
                      )}
                    </div>
                  )}
                  <div className={shareholderForm.watch("type") !== "person" ? "sm:col-span-2" : ""}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Antal aktier *
                    </label>
                    <input
                      type="text"
                      {...shareholderForm.register("shareQuantity")}
                      className="input"
                      placeholder={remainingShares > 0 ? String(remainingShares) : "0"}
                    />
                    {shareholderForm.formState.errors.shareQuantity && (
                      <p className="text-sm text-red-600 mt-1">
                        {shareholderForm.formState.errors.shareQuantity.message}
                      </p>
                    )}
                    {remainingShares > 0 && editingFounderIndex === null && (
                      <p className="text-xs text-blue-600 mt-1">
                        {remainingShares.toLocaleString("sv-SE")} aktier återstår att fördela
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adress
                  </label>
                  <input
                    type="text"
                    {...shareholderForm.register("address")}
                    className="input"
                    placeholder="Gatuadress"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postnummer
                    </label>
                    <input
                      type="text"
                      {...shareholderForm.register("postalCode")}
                      className="input"
                      placeholder="123 45"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ort
                    </label>
                    <input
                      type="text"
                      {...shareholderForm.register("city")}
                      className="input"
                      placeholder="Stockholm"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-post
                    </label>
                    <input
                      type="email"
                      {...shareholderForm.register("email")}
                      className="input"
                      placeholder="namn@exempel.se"
                    />
                    {shareholderForm.formState.errors.email && (
                      <p className="text-sm text-red-600 mt-1">
                        {shareholderForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      {...shareholderForm.register("phone")}
                      className="input"
                      placeholder="070-123 45 67"
                    />
                  </div>
                </div>

                {/* Form actions */}
                <div className="flex justify-end gap-2 pt-2">
                  {editingFounderIndex !== null && (
                    <Button type="button" variant="outline" onClick={handleCancelEdit}>
                      Avbryt
                    </Button>
                  )}
                  <Button type="submit">
                    <Plus size={16} className="mr-1" />
                    {editingFounderIndex !== null ? "Uppdatera" : "Lägg till grundare"}
                  </Button>
                </div>
              </div>
            </div>
          </form>

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <Button type="button" variant="outline" onClick={goBack}>
              <ChevronLeft size={16} className="mr-1" />
              Tillbaka
            </Button>
            <Button
              type="button"
              onClick={handleStep3Continue}
              disabled={founders.length === 0 || !allSharesAssigned}
            >
              Nästa
              <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Confirmation */}
      {currentStep === 4 && shareBookData && shareClassData && founders.length > 0 && (
        <div>
          <div className="space-y-6">
            {/* Share Book Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Aktiebok</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Aktiekapital:</span>
                  <span className="font-medium">
                    {Number(shareBookData.shareCapital).toLocaleString("sv-SE")} kr
                  </span>
                </div>
                {(shareBookData.hasPreemptionClause ||
                  shareBookData.hasConsentClause ||
                  shareBookData.hasRedemptionClause ||
                  shareBookData.hasConversionClause) && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Förbehåll:</span>
                    <span className="font-medium text-right">
                      {[
                        shareBookData.hasPreemptionClause && "Förköp",
                        shareBookData.hasConsentClause && "Samtycke",
                        shareBookData.hasRedemptionClause && "Inlösen",
                        shareBookData.hasConversionClause && "Omvandling",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Share Class Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Aktieslag</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Namn:</span>
                  <span className="font-medium">{shareClassData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Kod:</span>
                  <span className="font-medium">{shareClassData.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Antal aktier:</span>
                  <span className="font-medium">
                    {Number(shareClassData.totalShares).toLocaleString("sv-SE")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Röststyrka:</span>
                  <span className="font-medium">{shareClassData.votingPower} röst/aktie</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Kvotvärde:</span>
                  <span className="font-medium">
                    {(Number(shareBookData.shareCapital) / Number(shareClassData.totalShares)).toFixed(2)} kr/aktie
                  </span>
                </div>
              </div>
            </div>

            {/* Founders Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Grundare ({founders.length})
              </h3>
              <div className="space-y-3">
                {founders.map((founder, index) => (
                  <div
                    key={founder.id}
                    className={`p-3 bg-white rounded border border-gray-200 ${
                      index > 0 ? "" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-900">{founder.name}</span>
                        <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {SHAREHOLDER_TYPE_LABELS[founder.type]}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900">
                          {Number(founder.shareQuantity).toLocaleString("sv-SE")} aktier
                        </div>
                        <div className="text-xs text-gray-500">
                          {((Number(founder.shareQuantity) / Number(shareClassData.totalShares)) * 100).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                    {(founder.personalId || founder.orgNumber) && (
                      <div className="mt-1 text-xs text-gray-400">
                        {founder.personalId || founder.orgNumber}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>OBS:</strong> Efter att aktieboken skapats registreras {founders.length === 1 ? "en" : founders.length} {" "}
                bildandetransaktion{founders.length > 1 ? "er" : ""} automatiskt. Du kan sedan lägga till fler aktieägare
                och aktieslag.
              </p>
            </div>
          </div>

          <div className="flex justify-between mt-8">
            <Button type="button" variant="outline" onClick={goBack} disabled={isSubmitting}>
              <ChevronLeft size={16} className="mr-1" />
              Tillbaka
            </Button>
            <Button
              type="button"
              onClick={handleFinalSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Skapar...
                </>
              ) : (
                <>
                  Skapa aktiebok
                  <Check size={16} className="ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
